import { AddRounded, Delete, Preview, Save } from '@mui/icons-material'
import { Box, Button, Card, CircularProgress, Drawer, IconButton, Modal, Stack, TextField, Tooltip } from '@mui/material'
import { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import ApiClient from '../../backend/ApiClient'
import { IPreviewURI, IReport, IReportSection, ISectionColumn, ReportSectionType, SectionColumnType } from '../../definitions/report_builder/ReportBuilderDefinitions'
import Helper from '../../helpers/Helper'
import { useAppSelector } from '../../store/Hooks'
import { changeRoute, overwriteRouteParams } from '../../store/slices/SessionSlice'
import useCommand from '../hooks/useCommand'
import useInvalidate from '../hooks/useInvalidate'
import { buildQueryKey } from '../hooks/useQuery'
import PermissionsWrapper from '../permissions/PermissionsWrapper'
import ReportPreview from '../reports/ReportPreview'
import ReportSectionRenderer from '../reports/ReportSectionRenderer'
import SectionDetailEditor from '../reports/SectionDetailEditor'
import RoutingManager from '../routing/Routing'
import { modals } from '../services/ModalService'
import { snackbar } from '../services/SnackbarService'

export const ReportEditor = () => {
    const dispatch = useDispatch()
    const { report: originalReport } = RoutingManager.getParams<{ report: IReport }>(useAppSelector(s => s.session.routeParams))
    const [report, setReport] = useState<IReport>(originalReport)
    const [isDrawerOpen, setDrawerOpen] = useState(false)
    const [selectedSection, setSelectedSection] = useState<IReportSection>()
    const [openModal, setOpenModal] = useState(false)
    const [pdfPreview, setPDFPreview] = useState<IPreviewURI['url']>(null)
    const [draggedSection, setDraggedSection] = useState<IReportSection>(null)
    const [dragOverIndex, setDragOverIndex] = useState<number>(null)
    const invalidateApprovals = useInvalidate({ queryKey: buildQueryKey('get-approvals') })
    const invalidate = useInvalidate({ queryKey: buildQueryKey('reports') })
    const isDirty = useMemo(() => JSON.stringify(report) !== JSON.stringify(originalReport), [report, originalReport])
    const handleSelectLayout = (section: IReportSection, type: ReportSectionType) => {
        setReport(prev => ({
            ...prev, sections: prev.sections.map(x =>
                x.uuid === section.uuid
                    ? {
                            ...x,
                            type,
                            columns: Array.from({ length: Number(type[0]) }).map(() => ({
                                uuid: Helper.generateUUID(),
                                type: 'text',
                                settings: {}
                            }))
                        }
                    : x
            )
        }))
    }

    const handleSelectColumnType = (section: IReportSection, column: ISectionColumn, type: SectionColumnType) => {
        setReport(prev => ({
            ...prev, sections: prev.sections.map(x =>
                x.uuid === section.uuid
                    ? {
                            ...x,
                            columns: x.columns.map(col =>
                                col.uuid === column.uuid
                                    ? { ...col, type }
                                    : col
                            )
                        }
                    : x
            )
        }))
    }

    const handleAddSection = () => {
        const newSection: IReportSection = {
            uuid: Helper.generateUUID(),
            type: '1-column',
            columns: [{ uuid: Helper.generateUUID(), type: 'text', settings: {} }]
        }
        setReport(prev => ({ ...prev, sections: [...(prev?.sections ?? []), newSection] }))
    }

    const handleDeleteSection = (section: IReportSection) => {
        setReport(prev => ({ ...prev, sections: prev.sections.filter(x => x.uuid !== section.uuid) }))
    }

    const handleDragStart = (e: React.DragEvent, section: IReportSection) => {
        setDraggedSection(section)
        e.dataTransfer.effectAllowed = 'move'

        const originalElement = e.currentTarget as HTMLElement
        const dragImage = originalElement.cloneNode(true) as HTMLElement

        dragImage.style.position = 'absolute'
        dragImage.style.top = '-1000px'
        dragImage.style.left = '-1000px'
        dragImage.style.width = `${Math.min(originalElement.offsetWidth, 1100)}px`
        dragImage.style.height = `${Math.min(originalElement.offsetHeight, 400)}px`
        dragImage.style.transform = 'rotate(2deg)'
        dragImage.style.opacity = '0.8'
        dragImage.style.pointerEvents = 'none'
        dragImage.style.overflow = 'hidden'
        dragImage.style.boxShadow = '0 4px 12px theme.palette.grey[500]'
        dragImage.style.borderRadius = '8px'
        dragImage.style.zIndex = '9999'

        document.body.appendChild(dragImage)

        const rect = originalElement.getBoundingClientRect()
        const offsetX = Math.min(rect.width, 1200) / 2 // Center the drag image horizontally
        const offsetY = Math.min(rect.height, 100) / 2

        e.dataTransfer.setDragImage(dragImage, offsetX, offsetY)

        setTimeout(() => {
            if (document.body.contains(dragImage)) {
                document.body.removeChild(dragImage)
            }
        }, 100)
    }

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        setDragOverIndex(index)
    }

    const handleDragLeave = () => {
        setDragOverIndex(null)
    }

    const handleDrop = (e: React.DragEvent, targetIndex: number) => {
        e.preventDefault()

        if (!draggedSection) return

        const sections = [...report.sections]
        const draggedIndex = sections.findIndex(s => s.uuid === draggedSection.uuid)

        if (draggedIndex === targetIndex) return

        sections.splice(draggedIndex, 1)

        const adjustedTargetIndex = draggedIndex < targetIndex ? targetIndex - 1 : targetIndex
        sections.splice(adjustedTargetIndex, 0, draggedSection)

        setReport(prev => ({ ...prev, sections }))
        setDraggedSection(null)
        setDragOverIndex(null)
    }

    const handleDragEnd = () => {
        setDraggedSection(null)
        setDragOverIndex(null)
    }

    const { execute, status } = useCommand({
        commandFn: () => ApiClient.updateReport(report),
        onFinish: result => {
            if (result.status === 'success') {
                invalidate()
                snackbar.show({ text: `Report ${report.name} successfully saved`, severity: 'success' })
                setReport(result.response)
                dispatch(overwriteRouteParams({ report: result.response }))
            }
        }
    })

    const { execute: executeDelete, status: statusDelete } = useCommand({
        commandFn: () => modals.openConfirm({
            title: 'Confirm File Deletion',
            children: <p>Are you sure you want to delete this file?</p>,
            labels: { confirm: 'Delete', cancel: 'Cancel' },
            colors: { confirm: 'error', cancel: 'primary' },
            onConfirm: async () => {
                try {
                    await ApiClient.deleteReport(report.uuid)
                    invalidate()
                    invalidateApprovals()
                    dispatch(changeRoute({ route: 'contracts', params: { tab: 'reports' } }))
                    snackbar.show({ severity: 'success', text: `Report ${report.name} deleted successfully` })
                } catch (error) {
                    snackbar.show({ severity: 'error', text: `Unable to delete report ${report.name}` })
                    console.error(error)
                    return
                }
            }
        })
    })

    const { execute: executeGeneratePreview, status: statusPreview } = useCommand({
        commandFn: () => ApiClient.generatePreview(report),
        onFinish: result => {
            if (result.status === 'success') {
                setPDFPreview(result.response.url)
            }
        }
    })

    const preview = () => {
        setOpenModal(true)
        executeGeneratePreview(null)
    }

    return (
        <Stack spacing={2}>
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <TextField
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: 'transparent'
                            },
                            '&:hover fieldset': {
                                borderColor: 'dimgray'
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: 'dimgray'
                            },
                            '& input': {
                                padding: 'var(--unit)',
                                fontSize: '2rem',
                                fontWeight: 500,
                                lineHeight: 1.2
                            }
                        }
                    }}
                    InputProps={{
                        style: {
                            height: 'unset'
                        }
                    }}
                    variant='outlined'
                    defaultValue={report?.name}
                    onChange={e => setReport({ ...report, name: e.target.value })}
                />
                <Stack direction='row' spacing={2}>
                    <PermissionsWrapper permission='report-editor.delete'>
                        <Button
                            onClick={executeDelete}
                            startIcon={statusDelete === 'loading' ? <CircularProgress size={20} /> : <Delete />}
                            variant='outlined'
                            color='error'>Delete
                        </Button>
                    </PermissionsWrapper>
                    <Button onClick={preview} startIcon={<Preview />} variant='outlined'>Preview</Button>
                    <Button
                        onClick={execute}
                        startIcon={(
                            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                {status === 'loading'
                                    ? (
                                            <CircularProgress color='inherit' size={20} />
                                        )
                                    : (
                                            <Save />
                                        )}
                                {isDirty && (
                                    <Box
                                        component='span'
                                        sx={{
                                            position: 'absolute',
                                            top: -2,
                                            right: -2,
                                            width: 6,
                                            height: 6,
                                            bgcolor: 'orange',
                                            borderRadius: '50%',
                                            boxShadow: 1
                                        }}
                                    />
                                )}
                            </Box>
                        )}
                        variant='contained'
                        sx={{ position: 'relative' }}>
                        Save Template
                    </Button>
                </Stack>
            </Stack>

            {report?.sections?.map((section, index) => (
                <Box
                    key={section.uuid}
                    sx={{
                        'position': 'relative',
                        'transition': 'all 0.2s ease-in-out',
                        'transform': dragOverIndex === index ? 'translateY(-4px)' : 'translateY(0)',
                        '&::before': dragOverIndex === index
                            ? {
                                    content: '""',
                                    position: 'absolute',
                                    top: -2,
                                    left: 0,
                                    right: 0,
                                    height: 4,
                                    backgroundColor: 'primary.main',
                                    borderRadius: 2,
                                    zIndex: 1000
                                }
                            : {}
                    }}
                    onDragOver={e => handleDragOver(e, index)}
                    onDragLeave={handleDragLeave}
                    onDrop={e => handleDrop(e, index)}>
                    <Box
                        draggable
                        onDragStart={e => handleDragStart(e, section)}
                        onDragEnd={handleDragEnd}
                        sx={{
                            'cursor': 'grab',
                            '&:active': {
                                cursor: 'grabbing'
                            },
                            'transition': 'opacity 0.2s ease-in-out',
                            'border': 'none'
                        }}>
                        <Card sx={{
                            'position': 'relative',
                            '&:hover': {
                                '.report-section-commands': { opacity: 1 },
                                '.drag-handle': { opacity: 1 },
                                'boxShadow': '10px 0 15px rgba(10, 177, 237, 0.2), -10px 0 15px rgba(20, 197, 247, 0.15)',
                                'border': 'none'

                            }
                        }}>
                            <ReportSectionRenderer
                                section={section}
                                index={index}
                                handleSelectSection={x => {
                                    setSelectedSection(x)
                                    setDrawerOpen(true)
                                }}
                                handleCloneSection={x => {
                                    const newSection = structuredClone(x)
                                    newSection.uuid = Helper.generateUUID()
                                    newSection.columns = newSection.columns.map(c => ({ ...c, uuid: Helper.generateUUID() }))
                                    setReport(prev => ({ ...prev, sections: [...prev.sections, newSection] }))
                                    setSelectedSection(null)
                                    setDrawerOpen(false)
                                }}
                                handleDeleteSection={handleDeleteSection}
                                handleSelectColumnType={handleSelectColumnType}
                                handleSelectLayout={handleSelectLayout}
                            />
                        </Card>
                    </Box>
                </Box>
            ))}

            <Stack direction='row' justifyContent='center'>
                <Tooltip title='Add section' arrow>
                    <IconButton
                        size='large'
                        sx={{ 'backgroundColor': 'primary.main', '&:hover': { backgroundColor: 'primary.dark' } }}
                        onClick={handleAddSection}>
                        <AddRounded />
                    </IconButton>
                </Tooltip>
            </Stack>

            <Drawer
                open={isDrawerOpen}
                anchor='right'
                onClose={() => {
                    setDrawerOpen(false)
                    setSelectedSection(null)
                }}
                PaperProps={{ sx: { width: '50%', borderRadius: 0 } }}>
                <SectionDetailEditor
                    section={selectedSection}
                    index={report?.sections?.findIndex(x => x.uuid === selectedSection?.uuid) ?? 0}
                    onChange={section => {
                        setSelectedSection(section)
                        setReport(prev => ({
                            ...prev, sections: prev.sections.map(x =>
                                x.uuid === section.uuid
                                    ? section
                                    : x
                            )
                        }))
                    }}
                />
            </Drawer>

            <Modal
                open={openModal}
                onClose={() => {
                    setOpenModal(false)
                    setPDFPreview(null)
                }}
                sx={{ margin: 'auto auto', height: '80vh', width: 'calc(90vw - var(--left-nav-width))', left: 'var(--left-nav-width)' }}>
                <ReportPreview
                    onClose={() => {
                        setOpenModal(false)
                        setPDFPreview(null)
                    }}
                    pdfLink={pdfPreview}
                    status={statusPreview}
                />
            </Modal>
        </Stack>
    )
}
